// FocusChat — a plain, free-form "chat with Loom" inside the Focus surface.
//
// Unlike Create / Refine / Implement, there is NO task and NO readiness gate:
// the user just talks to the project's PM agent. It holds the project's CHAT
// session — its own slot next to the one issue work shares — renders the live
// timeline as a conversation thread, and sends each message straight through
// `useWsSession.send`. The chat is the backbone; this is only the skin — same
// as the rest of Focus.
//
// One thing of the card does reach this surface: when the user refines an issue
// here, the `draft` Loom attached to each open story field and list is pinned
// above the composer (`ChatProposalBlock`), each one click from being the user's
// own answer. Those clicks are held and go out as ONE ordinary chat message —
// `ws.send(text)` with nothing else, never a `flow` — see `sendChat` below.

import { Fragment, useEffect, useMemo, useRef, useState } from "react";

import { uiProse } from "@loom/shared/brand";

import { awaitIdleDelete } from "../chat/idle-delete";
import { isSessionDead } from "../chat/session-status";
import { entrySeq } from "../chat/timeline";
import { useWsSession, type TimelineEntry } from "../chat/useWsSession";
import { IdleReconnectDialog } from "../chat/IdleReconnectDialog";
import { loomConfirmComponents } from "../chat/render/AssistantTurn";
import {
  appendToComposer,
  chatNameLineOf,
  composeChatBatch,
  composerNamesField,
} from "../chat/render/chatAnswer";
import { useAnswerHold, type HeldBatch } from "../chat/render/useAnswerHold";
import {
  acceptedDisplayKey,
  acceptedIssueOf,
} from "../chat/render/acceptMessage";
import { MarkdownView } from "../dashboard/components/MarkdownView";
import {
  useProjectSession,
  clearProjectSessionBinding,
  pendingProjectSession,
  readProjectSessionId,
} from "../dashboard/useProjectSession";
import { useT } from "../i18n/index.js";
import { reportClientEvent } from "../lib/client-trace";
import { reconnectBody } from "../lib/reconnect-body";
import { detachSession, useSessionBridgeEnsure } from "../lib/sessionBeacon";

import { BREAKDOWN_MESSAGE_PREFIX } from "../breakdown/breakdownMessage";
import { ChatProposalBlock, chatProposalRows } from "./ChatProposalBlock";
import { FigmaLinkPanel } from "./figma/FigmaLinkPanel";
import { placeFigmaLinks } from "./figma/placement";
import {
  FocusFrame,
  Headline,
  Help,
  Kicker,
  MessageBar,
  ThinkingDots,
  type FocusStatus,
} from "./ui";
import { accessDeniedHelpKey } from "./useProjectAccess";
import { errorText } from "./errorCopy";

interface FocusChatProps {
  readonly projectId: number;
  readonly onBack: () => void;
  /**
   * Start a new chat. REQUIRED, and owned by the host: this surface holds its
   * own `useProjectSession("chat")` state, and a reset fired from here would
   * leave the host's instance — the one the pagehide beacon reads — pinned to
   * the dead id.
   */
  readonly onNewChat: () => void;
}

// Sandbox phases with a localized pill label (`focus.sandbox.<phase>`); the
// display text lives in the message catalog. An unmapped phase falls through to
// the call site's own fallback copy.
const SANDBOX_PHASES = new Set<string>([
  "idle",
  "triggering",
  "waiting-for-runner",
  "booting",
  "ready",
  "degraded",
  "suspended",
  "terminated",
]);

/**
 * The composer's hold while a paused chat wakes up.
 *
 * The idle-killed state has no dedicated UI to click through: the user types,
 * presses Send, and their own bytes stay in the box they typed them into —
 * read-only, same DOM node, caret intact — until the server's `user_message`
 * echo puts the real row on screen. The wait is a reconnect round trip, not a
 * cold boot: `POST /reconnect` seeds the bus and lazy-loads the session before
 * it returns, the server emits the echo at frame ingress before the router ever
 * consults the bridge, and the ANSWER comes from the in-process servant while
 * the sandbox is still `triggering` / `booting`.
 *
 * `text` rides every phase because it is the only copy that exists — `send`
 * appends nothing optimistically, and on `wakeFailed` nothing was ever sent.
 */
type Hold =
  | {
      readonly phase: "waking";
      readonly text: string;
      readonly since: number;
      /**
       * Set while a Send on a STOPPED workspace waits for its `POST
       * /reconnect`. The socket is still open then — the pause is the
       * sandbox's, not the chat's — so without this the flush below would send
       * the message into the stopped instance before the restart it is
       * waiting for. Cleared when the POST lands.
       */
      readonly restart?: true;
      /**
       * The answers held from Loom's proposal ride this message. They stay in
       * the answer hold — visible, with Undo — until the frame actually goes
       * out, and are composed into it THEN: a wake that fails sends nothing,
       * and answers taken out of the hold at Send would be lost with it.
       */
      readonly carryHeld?: true;
    }
  | {
      readonly phase: "sent";
      readonly text: string;
      readonly clientMsgId: string;
      readonly since: number;
    }
  | { readonly phase: "wakeFailed"; readonly text: string }
  | {
      readonly phase: "stalled";
      readonly text: string;
      readonly clientMsgId: string;
    };

/**
 * The whole wake's deadline, measured from the Send that started it — the
 * `awaitIdleDelete` settle AND the `POST /reconnect` together, not the fetch
 * alone. One clock, because the `waking → wakeFailed` backstop runs off the
 * same instant: two origins meant the backstop could publish "couldn't reopen"
 * over a POST that was still in flight and about to succeed.
 */
const WAKE_MAX_MS = 30_000;
/**
 * How long the settle handle for the in-flight idle DELETE may hold the wake
 * up. The handle is a plain promise over a `fetch` with no timeout of its own,
 * so a tab that suspended mid-request can leave it pending forever — and every
 * later Send would then park behind it having triggered nothing.
 */
const IDLE_DELETE_MAX_MS = 5_000;
/** Dispatched into an open socket, then neither echoed nor acked. */
const SEND_STALL_MS = 45_000;
/** `waitMs`'s server-side ceiling (`http/client-trace.ts`). Over it, the entry
 *  fails the schema, which fails the array, which 400s the WHOLE batch — every
 *  unrelated `frame_send` in it included. A frozen tab can outrun any client
 *  deadline, so the report is clamped rather than trusted. */
const TRACE_MS_MAX = 3_600_000;

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

/**
 * The unsent paragraph, kept across a remount.
 *
 * PROJECT-keyed, because the one exit from a refused wake is a NEW chat with a
 * different session id and carrying the text into it is the point. SESSION-
 * STAMPED, because project-keyed alone leaks: a history-rail pick remounts the
 * whole surface under the same project with a different session, and an
 * unstamped record would seed last week's conversation with this week's
 * sentence. Restored only when the stamp matches (same chat after an F5) or
 * when `handoff` says the user themselves carried it here.
 */
interface DraftRecord {
  readonly sessionId: string | null;
  readonly text: string;
  readonly handoff?: true;
}

const draftKeyOf = (projectId: number): string =>
  `loom.chat.draft:${String(projectId)}`;

const readDraft = (projectId: number): DraftRecord | null => {
  try {
    const raw = window.sessionStorage.getItem(draftKeyOf(projectId));
    if (raw === null) return null;
    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed !== "object" ||
      parsed === null ||
      typeof (parsed as { text?: unknown }).text !== "string"
    )
      return null;
    const sid = (parsed as { sessionId?: unknown }).sessionId;
    return {
      sessionId: typeof sid === "string" ? sid : null,
      text: (parsed as { text: string }).text,
      ...((parsed as { handoff?: unknown }).handoff === true
        ? { handoff: true as const }
        : {}),
    };
  } catch {
    return null;
  }
};

/** An empty draft is no draft: the record is removed, never stored blank. */
const writeDraft = (projectId: number, record: DraftRecord): void => {
  try {
    if (record.text === "") {
      window.sessionStorage.removeItem(draftKeyOf(projectId));
      return;
    }
    window.sessionStorage.setItem(
      draftKeyOf(projectId),
      JSON.stringify(record),
    );
  } catch {
    /* sessionStorage unavailable — the draft just doesn't survive a remount */
  }
};

interface ChatRow {
  readonly id: string;
  /** "you" / "loom" are real turns; "meta" is light activity (files, gates);
   *  "seed" is a synthetic user_message — Loom's own machine-composed brief
   *  to itself, stored as a user turn so the model reads it as the opening of
   *  the conversation, but not the user's words. */
  readonly role: "you" | "loom" | "meta" | "seed";
  readonly text: string;
  /** "seed" only: the byte-exact span of `text` the user actually typed (may
   *  be absent — an issue picked from search declares none, see
   *  `makeRefineSeed`). */
  readonly evidence?: string;
  /** "you" / "seed" only: the bus seq of the `user_message` this row renders —
   *  what a Figma chip's `sourceSeq` names. */
  readonly seq?: number;
  /** "loom" only: the text names Loom's own machinery
   *  ({@link isProcessNarrationLeak}). Such a row is shown only when it is the
   *  reply the user is owed — see {@link visibleChatRows}. */
  readonly narration?: true;
  /** "meta" only: the row is an `error` entry. It renders catalog copy for
   *  `code` (`errorText`), never `text`, which is the wire message. */
  readonly error?: { readonly code?: string; readonly localized?: true };
}

/**
 * Internal-only vocabulary that must never reach a user (the "pointers not
 * prose" rule the readiness gate is built on). A genuine
 * chat answer has no reason to say "readiness card": it names a mechanism
 * this UI deliberately never surfaces anywhere else, so its presence in an
 * assistant turn IS the leak, not a hint of one. Narrower than filtering on
 * sentence shape ("I'll wait for…") — the model's own phrasing of ordinary
 * process narration is not controlled vocabulary and a shape heuristic would
 * just as happily eat a genuine answer that happens to start the same way.
 */
const isProcessNarrationLeak = (text: string): boolean =>
  text.toLowerCase().includes("readiness card");

/**
 * The rows to render: every row, less the narration that is NOT the reply to
 * the user's last message.
 *
 * Dropping every "readiness card" turn outright hid replies. In a chat the user
 * asks to refine a ticket, the agent's ANSWER names the card as often as its
 * narration does, and live on dev a user asked "Where is the Accept button?"
 * and then "Why is the User field not saving?" and saw nothing at all, twice,
 * with no thinking indicator (session 53f79106: both replies are in the log at
 * seq 401 and 456). A missing reply is worse than a word the user does not
 * know: they wait, re-ask, and conclude Loom is broken.
 *
 * So a narration row is still dropped mid-turn — while the agent works, and
 * whenever a later reply follows it before the user's next message — and it is
 * kept when it is the LAST Loom row of a finished turn: the reply. Pure;
 * unit-tested.
 */
export const visibleChatRows = <R extends Pick<ChatRow, "role" | "narration">>(
  rows: ReadonlyArray<R>,
  busy: boolean,
): R[] => {
  const keep = rows.map((r) => r.narration !== true);
  // Walk each run of rows between two user turns; the last "loom" row of a run
  // is its reply. The final run is only a finished reply once the agent is idle.
  let lastLoom = -1;
  const settle = (closed: boolean): void => {
    if (lastLoom >= 0 && closed) keep[lastLoom] = true;
    lastLoom = -1;
  };
  rows.forEach((r, i) => {
    if (r.role === "you" || r.role === "seed") settle(true);
    else if (r.role === "loom") lastLoom = i;
  });
  settle(!busy);
  return rows.filter((_, i) => keep[i]);
};

/** Every refine seed persisted for this project (any issue), as `text →
 *  evidence`. Read-only use of the storage contract `FocusPage.tsx` already
 *  documents (`seedStorageKey` / `Seed`) — a session picked out of the history
 *  rail can predate the chat slot and still carry a refine seed in its
 *  transcript, attributed to "You" with no way to tell it apart from what the
 *  user actually typed. Mirrors `FocusPage`'s own
 *  `readSeed` fallback (JSON `{text,evidence}`, else the raw legacy string)
 *  rather than importing it — that helper is module-private there and the
 *  contract is the persisted SHAPE, not the function. */
const collectSeedTexts = (
  projectId: number,
): ReadonlyMap<string, string | undefined> => {
  const seeds = new Map<string, string | undefined>();
  try {
    const prefix = `loom.refine.seed:${String(projectId)}:`;
    for (let i = 0; i < window.sessionStorage.length; i++) {
      const key = window.sessionStorage.key(i);
      if (key === null || !key.startsWith(prefix)) continue;
      const raw = window.sessionStorage.getItem(key);
      if (raw === null) continue;
      try {
        const parsed: unknown = JSON.parse(raw);
        if (
          typeof parsed === "object" &&
          parsed !== null &&
          typeof (parsed as { text?: unknown }).text === "string"
        ) {
          const evidence = (parsed as { evidence?: unknown }).evidence;
          seeds.set(
            (parsed as { text: string }).text,
            typeof evidence === "string" ? evidence : undefined,
          );
          continue;
        }
      } catch {
        /* legacy plain-string row — falls through to the raw-string case */
      }
      seeds.set(raw, undefined);
    }
  } catch {
    /* sessionStorage unavailable — no seeds to mark, every user_message
       renders as "you" exactly as before this fix */
  }
  return seeds;
};

/** Map a timeline entry to a chat row, or null to omit it. Mirrors the recap
 *  strip's filtering in RefineFlow: real turns plus light meta surface; raw
 *  tool calls and readiness cards are dropped as noise (the free chat has no
 *  gate, so a card is just clutter here). */
const rowOf = (
  e: TimelineEntry,
  seedTexts: ReadonlyMap<string, string | undefined>,
): ChatRow | null => {
  switch (e.kind) {
    case "user_message": {
      if (!e.text.trim()) return null;
      if (seedTexts.has(e.text))
        return {
          id: e.id,
          role: "seed",
          text: e.text,
          evidence: seedTexts.get(e.text),
          seq: entrySeq(e.id),
        };
      // A breakdown adjustment sent from the card's prose lane IS the user's
      // instruction, but self-labelled with a machine routing prefix (see
      // `BREAKDOWN_MESSAGE_PREFIX`) so the agent calls `propose_breakdown`
      // rather than `propose_card`. That prefix is wire protocol, not
      // something the user typed or would recognise — display-only strip,
      // the bytes actually sent (and what the agent routes on) are untouched.
      const text = e.text.startsWith(BREAKDOWN_MESSAGE_PREFIX)
        ? e.text.slice(BREAKDOWN_MESSAGE_PREFIX.length)
        : e.text;
      return { id: e.id, role: "you", text, seq: entrySeq(e.id) };
    }
    case "assistant_turn":
      if (!e.text.trim()) return null;
      return {
        id: e.id,
        role: "loom",
        text: e.text,
        ...(isProcessNarrationLeak(e.text) ? { narration: true as const } : {}),
      };
    case "role":
      return { id: e.id, role: "meta", text: `${e.role} — ${e.task}` };
    case "lifecycle":
      if (e.outcome.kind === "block")
        return { id: e.id, role: "meta", text: `blocked: ${e.outcome.reason}` };
      if (e.outcome.kind === "augment")
        return { id: e.id, role: "meta", text: e.outcome.systemNote };
      return null;
    case "coding_progress":
      switch (e.entry.kind) {
        case "user_prompt":
          // The bridge echoes the dispatched prompt as a `user_prompt`
          // activity entry once the sandbox is ready, but the user's turn is
          // already rendered from the canonical `user_message` event — drop
          // this to avoid double-rendering it (mirrors CodingProgress.tsx).
          return null;
        case "assistant_text":
          if (!e.entry.text.trim()) return null;
          return {
            id: e.id,
            role: "loom",
            text: e.entry.text,
            ...(isProcessNarrationLeak(e.entry.text)
              ? { narration: true as const }
              : {}),
          };
        case "file_touched":
          return { id: e.id, role: "meta", text: `${e.entry.op} ${e.entry.path}` };
        case "commit":
          return {
            id: e.id,
            role: "meta",
            // The model's commit message: under the UI public-name mode its
            // "Loom" shows as the public name (display only).
            text: `commit ${e.entry.sha.slice(0, 7)} — ${uiProse(e.entry.message)}`,
          };
        // DELIBERATE, and the opposite of what the refine flow does. Chat shows
        // the sandbox's raw shell command verbatim (`$ cat
        // /workspace/project/README.md`), which reads as a leak next to
        // RefineFlow's rule that no technical detail reaches the user.
        //
        // It is not the same surface and not the same promise. The refine flow
        // is an ELICITATION: it asks a business owner questions and its
        // suppression is structural — the prose the sandbox writes is dropped at
        // the render (see the `!card` branch there), because a sentence rendered
        // above an enabled composer is a sentence users answer, and an answer
        // typed there is unattributable. Chat has no gate, no queue and no
        // attributed lane: it is the "watch Loom work" surface, and these
        // `meta` rows — file touches, commits, commands — are the only evidence
        // it does anything between messages. Removing them leaves a spinner.
        //
        // What is NOT in question: the command's ARGUMENTS are model-authored
        // and unbounded, so this row is display-only. It never becomes a
        // quotable turn, never grounds a slot and never reaches a card.
        //
        // Reviewed against finding W and kept. Change it only together with a
        // decision about what replaces it — a bare spinner is worse.
        case "bash":
          return { id: e.id, role: "meta", text: `$ ${e.entry.command}` };
      }
      return null;
    case "error":
      return {
        id: e.id,
        role: "meta",
        text: e.message,
        error: {
          ...(e.code !== undefined ? { code: e.code } : {}),
          ...(e.localized ? { localized: true as const } : {}),
        },
      };
    default:
      // tool_call, proposal, budget_exhausted — and readiness_card, which is
      // never a transcript row here: its drafts are pinned above the composer
      // as Loom's proposal (`ChatProposalBlock`), and nothing else of the card
      // is shown in a chat.
      return null;
  }
};

export function FocusChat({
  projectId,
  onBack,
  onNewChat,
}: FocusChatProps) {
  // Chat is chat-only by BINDING, not by rendered rows. Filtering an issue
  // conversation out of the timeline would still leave the model holding all of
  // it as context, and `collectSeedTexts` below is what re-labelling someone
  // else's turns after the fact costs.
  const session = useProjectSession(projectId, "chat");
  const ws = useWsSession(session.sessionId);
  const t = useT();
  // The accept click is a protocol message carrying the acceptToken; a user row
  // shows what the user did instead (see `acceptedIssueOf`). An error row shows
  // the catalog line for its code, never the wire message.
  const userRowText = (m: ChatRow): string => {
    if (m.error !== undefined) return errorText({ message: m.text, ...m.error }, t);
    if (m.role !== "you") return m.text;
    const accepted = acceptedIssueOf(m.text);
    return accepted === null ? m.text : t(acceptedDisplayKey, { iid: accepted });
  };
  const phaseLabel = (phase: string): string | undefined =>
    SANDBOX_PHASES.has(phase)
      ? t(`focus.sandbox.${phase}` as Parameters<typeof t>[0])
      : undefined;

  // Boot (or adopt) the chat session once. `ensure` is idempotent and stable
  // per (projectId, slot) — a warm session picked up here serves the whole chat.
  const ensure = session.ensure;
  useEffect(() => {
    ensure();
  }, [ensure]);

  // An ADOPTED session skipped `POST /sessions`, so no pipeline was ever
  // triggered for it. Re-arm the bridge on open (no-op when it is already live).
  useSessionBridgeEnsure(session.sessionId);

  // Once the server calls the session dead (`expired` or `denied`), drop the
  // binding so nothing re-adopts the corpse.
  //
  // The MODULE CACHE, not just the sessionStorage mirror: `cachedSession` reads
  // the cache first, so the storage-only clear RefineFlow uses is invisible
  // until a full reload — and this surface has a way out that is not a reload.
  // A user who takes "Zurück" instead of "New chat" and re-enters the chat would
  // otherwise re-adopt the same revoked id from memory and land on this same
  // dead screen, for the life of the tab. Nothing here touches React state: this
  // mount keeps rendering the dead session, which is what puts the ended block
  // below on screen; only the NEXT mount cold-boots.
  useEffect(() => {
    if (isSessionDead(ws.status)) clearProjectSessionBinding(projectId, "chat");
  }, [ws.status, projectId]);

  // "Has this conversation happened yet." The server promotes the row to
  // `kind=chat` on a USER TURN (`onUserTurnKind`), so this is the same predicate
  // the rail's listing is built on — which is what makes `newChatNote`'s "stays
  // under Earlier chats" promise true by construction rather than by hope.
  // `rows.length > 0` is NOT that predicate: an error row, a lifecycle row or a
  // `coding_progress` meta row all render without a user turn.
  const spoke = ws.timeline.some((e) => e.kind === "user_message");

  // What the unmount detach below reads. One ref, three writers: this effect,
  // the send latch on the MessageBar, and the cleanup itself — never a captured
  // closure, which is the hazard in an unmount-only effect.
  //
  // `mintedHere` is the arm, and it is deliberately NOT a transport fact.
  // `ws.status === "open"` is set from the raw socket's `onOpen`, which fires
  // "even for a socket the server is about to reject" — before the route handler
  // has replayed anything — while `spoke` needs a REPLAYED `user_message`, which
  // for an evicted session costs a lazy-load plus an event-log decrypt. So
  // `open && !spoke` is routinely true for SECONDS on a conversation with a full
  // transcript, and arming on it would let a mis-click on Zurück hand a warm
  // sandbox and a real transcript to the grace reaper. What this step is
  // actually for is a session THIS mount caused: `ensure()` short-circuits on
  // the cache for an adopted or reloaded binding and sets `starting` only on a
  // real `POST /sessions`, so `starting` names exactly the pre-warm nobody has
  // spoken to yet — with no dependence on replay timing.
  const boundRef = useRef<{
    sessionId: string | null;
    spoke: boolean;
    mintedHere: boolean;
    /** Is this surface still on screen? Only the deferred in-flight leg reads
     *  it — see the cleanup below. */
    live: boolean;
  }>({ sessionId: null, spoke: false, mintedHere: false, live: false });
  useEffect(() => {
    const prev = boundRef.current;
    // `null → id` is the SAME binding continuing (the mint landing), so both
    // latches carry across it. A move to a DIFFERENT id is a new binding and
    // starts unspoken and un-minted — otherwise a fresh chat started after a
    // spoken one inherits both and is never reclaimed.
    const continues =
      prev.sessionId === null || prev.sessionId === session.sessionId;
    boundRef.current = {
      ...prev,
      sessionId: session.sessionId,
      spoke: (continues && prev.spoke) || spoke,
      mintedHere: (continues && prev.mintedHere) || session.starting,
    };
  }, [session.sessionId, session.starting, spoke]);

  // A pre-warmed chat nobody spoke to does not outlive the surface.
  //
  // `?reason=pagehide` and nothing else: `markDetached` puts the sandbox in the
  // server's 90s detach grace, whose reaper flushes the event log, cancels the
  // GitLab pipeline and evicts. The binding is deliberately NOT cleared — a
  // re-attach inside the grace clears the detach mark, so a bounce out and
  // straight back costs nothing, while clearing would guarantee a second
  // `POST /sessions` on return.
  //
  // The trade, stated: an abandoned chat saves up to the 60-minute idle horizon
  // of billed residency; a chat left for more than 90s and returned to pays one
  // extra cold boot. That is acceptable ONLY because `mintedHere && !spoke`
  // confines it to a pre-warm THIS mount caused and nobody has spoken to — there
  // is nothing to lose and the boot costs what the first one cost. An adopted or
  // reloaded binding never sets `starting`, so a resumed conversation is out of
  // reach of this path even while its transcript is still replaying.
  //
  // The in-flight half is covered too, and it has to be: `POST /sessions` waits
  // on GitLab for hundreds of ms to seconds, has already triggered the pipeline
  // by the time it lands, and the fastest abandonment (mount, then Zurück) falls
  // squarely inside it with `boundRef.sessionId` still null. `pendingProjectSession`
  // hands over the mint's own promise so the id can be detached after the fact —
  // re-checking the binding first, because a rail pick inside that window makes
  // the mint somebody else's problem (`ensure`'s generation guard detaches it).
  //
  // Residual: a mint that lands after the tab is GONE (close, not navigate) is
  // detached by neither path — nothing is running to await the promise — and
  // ages out on the idle horizon. A bounce straight back is benign either way:
  // the remount's attach calls `markUsed`, which clears the detach mark well
  // inside the 90s grace.
  useEffect(() => {
    boundRef.current.live = true;
    return () => {
      boundRef.current.live = false;
      const cur = boundRef.current;
      // `mintedHere` also carries the StrictMode arm: `main.tsx` wraps the tree
      // in `<React.StrictMode>`, whose simulated unmount runs this cleanup on
      // every dev mount, and `useProjectSession` seeds `sessionId` synchronously
      // from the sessionStorage mirror. That seeded id never went through
      // `POST /sessions`, so it is not `mintedHere` and no spurious DELETE
      // fires.
      if (!cur.mintedHere || cur.spoke) return;
      if (cur.sessionId !== null) {
        detachSession(cur.sessionId);
        return;
      }
      const pending = pendingProjectSession(projectId, "chat");
      if (pending === null) return;
      void pending
        .then((id) => {
          // StrictMode's simulated unmount runs this cleanup and then re-runs
          // the setup above on the SAME ref, so a surface that never actually
          // went away is `live` again long before the mint lands.
          if (boundRef.current.live) return;
          // Only the session this surface actually left behind. A binding that
          // moved under it (a rail pick) is `ensure`'s generation guard's to
          // detach, not ours.
          if (readProjectSessionId(projectId, "chat") !== id) return;
          detachSession(id);
        })
        .catch(() => {
          /* the mint failed — there is no session to detach */
        });
    };
  }, [projectId]);

  // ——— Wake on Send ——————————————————————————————————————————————————
  //
  // A paused chat keeps a LIVE composer. Send is what wakes the workspace, and
  // the user's sentence waits in the box until the row it belongs in exists.
  const [draft, setDraft] = useState("");
  const [hold, setHold] = useState<Hold | null>(null);
  const wakeInFlightRef = useRef(false);
  // The wake reads this AFTER an await, where the captured render value is a
  // lie — see the guard in `wake` below, which is the whole reason it exists.
  const statusRef = useRef(ws.status);
  statusRef.current = ws.status;

  // Restore once per mount. A STAMPED record waits for a session id to compare
  // against — restoring one unstamped IS the cross-chat leak.
  const draftRestoredRef = useRef(false);
  // Does the record belong to THIS mount? A mount that declines to restore
  // (another chat's stamp, no handoff) must not write either — the write turns
  // an empty draft into `removeItem`, so a rail pick or a second chat in the
  // same project would otherwise delete the first chat's unsent paragraph on
  // the way past. Set by a restore and by the user's first keystroke, which are
  // the only two ways this mount's `draft` becomes the record's content.
  const draftOwnedRef = useRef(false);
  useEffect(() => {
    if (draftRestoredRef.current) return;
    const stored = readDraft(projectId);
    if (stored === null) {
      draftRestoredRef.current = true;
      return;
    }
    // A handoff is restored on SIGHT, with no session id to wait for — it is
    // not stamped against one, and waiting is what leaks it. The record is
    // written by this project's own failed-wake block and consumed by the very
    // next mount; if that mount waited for `POST /sessions` and the mint failed
    // (or the user took Zurück inside it), the flag — the one shape ANY session
    // restores from — would still be in storage when the rail opened the next
    // conversation. Stripping it here is what makes it a same-chat draft, and
    // an unstamped record without the flag matches no session at all.
    if (stored.handoff === true) {
      draftRestoredRef.current = true;
      draftOwnedRef.current = true;
      setDraft(stored.text);
      writeDraft(projectId, {
        sessionId: session.sessionId,
        text: stored.text,
      });
      return;
    }
    if (session.sessionId === null) return;
    draftRestoredRef.current = true;
    if (stored.sessionId !== session.sessionId) return;
    draftOwnedRef.current = true;
    setDraft(stored.text);
  }, [projectId, session.sessionId]);

  useEffect(() => {
    if (!draftOwnedRef.current) return;
    // Re-stamped with the CURRENT id on every keystroke.
    writeDraft(projectId, { sessionId: session.sessionId, text: draft });
  }, [draft, projectId, session.sessionId]);

  const onDraftChange = (next: string): void => {
    draftOwnedRef.current = true;
    setDraft(next);
  };

  /** `startedAt` is the Send's own instant, so the wake and the backstop that
   *  gives up on it share one clock. */
  const wake = async (startedAt: number): Promise<void> => {
    if (wakeInFlightRef.current) return;
    wakeInFlightRef.current = true;
    reportClientEvent("idle_wake", { why: "send" });
    const elapsed = (): number => Math.min(Date.now() - startedAt, TRACE_MS_MAX);
    try {
      const sid = session.sessionId;
      if (sid === null) throw new Error("no_session");
      // The kill effect fires its `?reason=idle` DELETE un-awaited. A
      // `/reconnect` that overtakes it triggers a pipeline the DELETE then
      // cancels from behind — a session no sandbox ever boots into, with the
      // pill stuck and nothing logged as an error. Wait it out — but only for
      // as long as a DELETE plausibly takes. That fetch has no timeout, so a
      // suspended tab can leave it pending for good, and an unbounded wait here
      // is a wake that never even attempts the POST.
      await Promise.race([awaitIdleDelete(sid), sleep(IDLE_DELETE_MAX_MS)]);
      const ctl = new AbortController();
      // What is LEFT of the wake's budget, not a fresh 30s. The backstop fires
      // at `startedAt + WAKE_MAX_MS` whatever happened before this line.
      const deadline = setTimeout(
        () => ctl.abort(),
        Math.max(0, WAKE_MAX_MS - (Date.now() - startedAt)),
      );
      const res = await fetch(`/sessions/${sid}/reconnect`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: reconnectBody(),
        signal: ctl.signal,
      }).finally(() => clearTimeout(deadline));
      if (!res.ok) throw new Error(String(res.status));
      reportClientEvent("idle_wake", { why: "accepted", waitMs: elapsed() });
      // A restart's hold may flush now. The server has rebuilt the instance
      // and sent this socket to re-attach; a frame that still goes out on it
      // is refused unacked there and re-sent from the outbox after the
      // re-attach, so it reaches the new instance either way.
      setHold((h) =>
        h !== null && h.phase === "waking" && h.restart === true
          ? {
              phase: "waking",
              text: h.text,
              since: h.since,
              ...(h.carryHeld === true ? { carryHeld: true as const } : {}),
            }
          : h,
      );
      // The POST must land BEFORE the socket reopens: an attach that wins the
      // race rehydrates with `bridgeTriggered = false` (an in-memory field,
      // never persisted), so `/reconnect` misses its healthy-bridge early
      // return, removes the session from the live registry and rebuilds it
      // under an already-attached socket — the bus-nonce orphan.
      //
      // And the status guard is not caution. `resumeFromIdleKill` sets
      // `connecting` UNCONDITIONALLY but only re-runs the attach by flipping
      // `idleKilled`, and flipping an already-false value is a React no-op —
      // so calling it after the latch has cleared wedges the status at
      // `connecting` forever, with the composer disabled and no way back.
      if (statusRef.current === "idle_killed") ws.resumeFromIdleKill();
    } catch {
      reportClientEvent("idle_wake", { why: "refused", waitMs: elapsed() });
      // Nothing was sent on this path — `ws.send` is never reached — so there
      // is no frame anywhere and no duplicate is reachable. The text goes back
      // to the user editable, in the node it never left.
      setHold((h) =>
        h !== null && h.phase === "waking"
          ? { phase: "wakeFailed", text: h.text }
          : h,
      );
    } finally {
      wakeInFlightRef.current = false;
    }
  };

  const live = ws.sandbox.phase === "ready";
  const budgetDead = ws.budgetExhausted !== null;
  // A workspace that RAN and stopped — a pipeline behind it, now `terminated`.
  // One that never started (dispatch disabled, a refused trigger) has no
  // pipeline, and a restart would be refused the same way; there the
  // boot-window agent keeps answering, as it always did.
  const stoppedWorkspace =
    ws.status === "open" &&
    ws.sandbox.phase === "terminated" &&
    ws.sandbox.pipelineId !== undefined &&
    !budgetDead;
  // No workspace at all behind an open socket: the server rebuilt this chat
  // from its log after the resident one was reclaimed (a socket that stayed
  // dead long enough, an instance recycle), and only a `/reconnect` boots one
  // — which this surface sent once, on mount. Every turn since went to the
  // boot-window agent under a prompt promising a sandbox "~30s" away that
  // nobody had asked for: the colleague's "the sandbox is still starting up".
  // The same restart covers it; on a chat whose boot is merely in flight the
  // server's healthy-bridge early return makes it a no-op.
  const restartOnSend =
    stoppedWorkspace ||
    (ws.status === "open" && ws.sandbox.phase === "idle" && !budgetDead);

  // ——— Loom's proposal ——————————————————————————————————————————————————
  //
  // The LATEST card in the timeline, by position: a chat has no bound issue, so
  // there is no "the card for this issue" — only the one the agent touched last.
  const latestCard = useMemo(() => {
    for (let i = ws.timeline.length - 1; i >= 0; i -= 1) {
      const e = ws.timeline[i];
      if (e?.kind === "readiness_card") return e;
    }
    return null;
  }, [ws.timeline]);
  // A chat an issue already OWNS — any echoed turn carries an `issueIid` — is
  // routed under the REFINE directive, whose widget rule tells the model to
  // keep a labelled token verbatim. A name-lined answer there would be quoted
  // WITH its name line, and the ticket would read "As Role: …". So the block,
  // and the name-lined batch it sends, exist only in a chat no issue owns.
  const issueOwned = useMemo(
    () =>
      ws.timeline.some(
        (e) => e.kind === "user_message" && typeof e.issueIid === "number",
      ),
    [ws.timeline],
  );
  const proposalIid = latestCard?.issueIid ?? null;
  const proposalRows = useMemo(
    () =>
      latestCard === null || issueOwned ? [] : chatProposalRows(latestCard.verdicts),
    [latestCard, issueOwned],
  );
  const paused = ws.status === "idle_killed" || restartOnSend;
  // The id `onFlushAnswers` put on the wire last — the wake flush below needs
  // it for its own landed / stalled tracking, and `flushNow` returns a boolean.
  const lastFlushIdRef = useRef("");
  /**
   * The held answers' ONE message, on the lane a typed chat message takes:
   * `ws.send(text)` and nothing else. No `flow` — a bound turn in a plain chat
   * is a flow boundary: it drops the agent's whole conversation
   * (`claudeSessionId = null`), makes the issue own the chat
   * (`conversationIssueIid`) and turns strict issue scoping on, so every earlier
   * unbound answer would stop grounding. Never `question_answer` either: it
   * needs an issue, and `ensureFlowFor` would adopt the chat for it.
   */
  const onFlushAnswers = (batch: HeldBatch): string | null => {
    if (ws.status !== "open") return null;
    const text = composeChatBatch(
      batch.entries.map((e) => ({ field: e.field ?? "", text: e.text })),
      batch.freeText,
    );
    if (text === "") return null;
    boundRef.current = { ...boundRef.current, spoke: true };
    const id = ws.send(text);
    lastFlushIdRef.current = id;
    return id;
  };
  const answers = useAnswerHold({
    storageKey: `loom.chat.held:${String(projectId)}`,
    sessionId: session.sessionId,
    resetEpoch: ws.resetEpoch,
    // On their own, the held answers go out only into a live, idle chat whose
    // composer is empty: a message landing mid-turn kills that turn, a paused
    // or stopped chat must not wake without a Send, and a paragraph the user is
    // typing goes out WITH them on their Send rather than one turn behind.
    canFlush:
      ws.status === "open" &&
      // Idle only once the server has SAID so to this socket: `agentBusy` reads
      // false on a first load, and a restored batch flushed then could land in
      // the middle of a running turn and kill it.
      ws.busyKnown &&
      ws.sandbox.phase === "ready" &&
      !restartOnSend &&
      !budgetDead &&
      !ws.agentBusy &&
      hold === null &&
      draft.trim() === "",
    agentBusy: ws.agentBusy,
    openKeys:
      proposalIid === null
        ? []
        : proposalRows.map((r) => `${String(proposalIid)}:${r.field}`),
    // No card on screen (a replay that has not reached it yet) is not "every
    // row answered": the quiet timer decides then, never an empty `openKeys`.
    openElsewhere: latestCard === null,
    groundedThroughSeq: latestCard?.groundedThroughSeq,
    ackedIds: ws.ackedIds,
    // Nobody waits for an answer to a field that went green or was skipped, or
    // to anything on a card that was applied. Judged against the card for the
    // entry's OWN issue: a replay passes through older cards, and "the latest
    // card is another issue's" must not throw a click away. In a chat an issue
    // came to OWN, nothing held can go out named, and the block that showed it
    // is gone — so nothing held survives there.
    stale: (e) => {
      if (issueOwned) return true;
      const iid = Number(e.key.slice(0, e.key.indexOf(":")));
      if (ws.applyResult?.ok === true && ws.applyResult.issueIid === iid) return true;
      let card = null;
      for (let i = ws.timeline.length - 1; i >= 0; i -= 1) {
        const x = ws.timeline[i];
        if (x?.kind === "readiness_card" && x.issueIid === iid) {
          card = x;
          break;
        }
      }
      const v = card?.verdicts.find((x) => x.field === e.field);
      return v !== undefined && (v.verdict === "green" || v.declined === true);
    },
    onFlush: onFlushAnswers,
  });
  const heldFor = (iid: number | null): ReadonlyMap<string, string> => {
    const m = new Map<string, string>();
    if (iid === null) return m;
    for (const e of answers.held.values()) {
      if (e.field !== null && e.key === `${String(iid)}:${e.field}`) m.set(e.field, e.text);
    }
    return m;
  };
  const sentFor = (iid: number | null): ReadonlySet<string> => {
    const out = new Set<string>();
    if (iid === null) return out;
    const prefix = `${String(iid)}:`;
    for (const k of answers.sent) if (k.startsWith(prefix)) out.add(k.slice(prefix.length));
    return out;
  };

  // "Write my own" and "Change the list" put the text in the composer and the
  // caret at its end — after the render that shows it.
  const composerRef = useRef<HTMLTextAreaElement | null>(null);
  const caretToEndRef = useRef(false);
  useEffect(() => {
    if (!caretToEndRef.current) return;
    caretToEndRef.current = false;
    const el = composerRef.current;
    if (el === null) return;
    el.focus();
    el.setSelectionRange(el.value.length, el.value.length);
  }, [draft]);
  const putInComposer = (block: string): void => {
    caretToEndRef.current = true;
    onDraftChange(appendToComposer(draft, block));
  };

  /**
   * Send a chat message — the composer's, or the held answers alone ("Send
   * now") — through the one path that knows how to wake a paused chat and
   * restart a stopped one. With `carryHeld`, the held answers ride the SAME
   * message, composed where the frame actually goes out.
   */
  const sendChat = (text: string, carryHeld: boolean): void => {
    // One Send buys AT MOST one `POST /reconnect`, and a reconnect is a
    // real GitLab pipeline. `setHold` is synchronous inside the handler,
    // so a second ⌘Enter in the same window finds a non-null hold here.
    if (hold !== null && hold.phase !== "wakeFailed") return;
    const carry = carryHeld && answers.count > 0;
    if (!carry && text.trim() === "") return;
    const carryFlag = carry ? { carryHeld: true as const } : {};
    // The chat is connected but its WORKSPACE has stopped — GitLab ends
    // every sandbox job at 60 minutes, and this is where the colleague's
    // chat on #1154 went on without one: the boot-window agent answered,
    // could not save, and said so with "once it runs again" when nothing
    // would run it again. Send restarts it, exactly as it wakes a paused
    // chat, and the message goes out once the restart has landed.
    if (restartOnSend) {
      setHold({ phase: "waking", text, since: Date.now(), restart: true, ...carryFlag });
      void wake(Date.now());
      return;
    }
    // The happy path, untouched: an open socket sends and clears exactly
    // as it did before any of this existed.
    if (ws.status === "open") {
      boundRef.current = { ...boundRef.current, spoke: true };
      const tookHeld =
        carry && answers.flushNow(text.trim() === "" ? undefined : text);
      if (!tookHeld && text.trim() !== "") ws.send(text);
      setDraft("");
      setHold(null);
      return;
    }
    if (ws.status === "idle_killed") {
      setHold({ phase: "waking", text, since: Date.now(), ...carryFlag });
      void wake(Date.now());
      return;
    }
    // Only reachable as `wakeFailed` + `connecting`: the backstop gave up
    // on a POST that then succeeded and resumed the session. The socket is
    // already on its way, so this Send takes the flush — and NOT another
    // wake, which would buy a second pipeline for a reconnect already
    // paid for. Deliberately not widened to plain `closed`: that status
    // has no explanation on screen and no reconnect behind it.
    if (hold?.phase === "wakeFailed" && ws.status === "connecting")
      setHold({ phase: "waking", text, since: Date.now(), ...carryFlag });
  };
  const canSendHeld =
    (hold === null || hold.phase === "wakeFailed") &&
    !budgetDead &&
    (restartOnSend ||
      ws.status === "open" ||
      ws.status === "idle_killed" ||
      (hold?.phase === "wakeFailed" && ws.status === "connecting"));

  // Flush on the SOCKET, not on the sandbox: the server emits the user_message
  // echo at frame ingress, ahead of any routing, so the row does not wait on a
  // boot. Nothing enters the outbox before this point either, which is why the
  // wake window is not exposed to the outbox's TTL / scope / session-change
  // drops.
  useEffect(() => {
    if (
      hold === null ||
      hold.phase !== "waking" ||
      hold.restart === true ||
      ws.status !== "open"
    )
      return;
    // The held answers are composed into the message HERE, where it goes out,
    // not at the Send that started the wake (see `carryHeld`).
    let clientMsgId = "";
    lastFlushIdRef.current = "";
    const tookHeld =
      hold.carryHeld === true &&
      answers.count > 0 &&
      answers.flushNow(hold.text.trim() === "" ? undefined : hold.text);
    if (tookHeld) clientMsgId = lastFlushIdRef.current;
    else if (hold.text.trim() !== "") clientMsgId = ws.send(hold.text);
    // The turn is on the wire, so the unmount detach must not reclaim this
    // session — see the latch's own note on the composer below.
    boundRef.current = { ...boundRef.current, spoke: true };
    if (clientMsgId === "") {
      setHold(null);
      setDraft("");
      return;
    }
    setHold({ phase: "sent", text: hold.text, clientMsgId, since: Date.now() });
    // `answers` is a fresh object per render; its `flushNow` is stable and
    // `count` is what the branch reads.
  }, [hold, ws.status, ws.send, answers.count, answers.flushNow]);

  // A `wakeFailed` hold that lives to see an open socket is over. It can happen
  // without anyone pressing anything: the backstop gives up at
  // `WAKE_MAX_MS` and a POST that lands a heartbeat later still reopens the
  // chat. Without this the "couldn't reopen" block and its caption sit over a
  // working conversation for the life of the mount, and the composer never
  // leaves the failed branch. Nothing was sent on that path, so the honest
  // outcome is the ordinary live composer with the user's text still in it —
  // which is precisely what the failed caption told them to press Send on.
  useEffect(() => {
    if (hold?.phase !== "wakeFailed" || ws.status !== "open") return;
    setHold(null);
  }, [hold, ws.status]);

  // Two release signals, and neither alone is enough. The ack is the only one
  // on a server whose echo carries no `clientMsgId`; the timeline leg is the
  // only one on a replay that re-sends no ack, or against a server that never
  // advertised acks at all. Both are safe by the server's own ordering — the
  // bus emit strictly precedes the ack, and listeners fan out synchronously —
  // so the real row is on screen before either fires and the composer can never
  // clear ahead of it.
  const landed = useMemo(() => {
    if (hold === null || (hold.phase !== "sent" && hold.phase !== "stalled"))
      return false;
    if (ws.ackedIds.has(hold.clientMsgId)) return true;
    return ws.timeline.some(
      (e) => e.kind === "user_message" && e.clientMsgId === hold.clientMsgId,
    );
  }, [hold, ws.ackedIds, ws.timeline]);

  useEffect(() => {
    if (!landed) return;
    setDraft("");
    setHold(null);
  }, [landed]);

  // Wall-clock backstops, one per waiting phase. `waking` past WAKE_MAX_MS is
  // belt-and-braces behind the AbortController — which is armed off this same
  // instant, so reaching here means the wake wedged somewhere no abort gets to
  // (a settle handle that never settles, a fetch that ignores its signal).
  // `sent` past SEND_STALL_MS keeps the text held and says so, because the
  // frame is in the outbox under its original id and a fresh Send would mint a
  // new one straight past the server's ingress dedupe.
  useEffect(() => {
    if (hold === null || hold.phase === "wakeFailed" || hold.phase === "stalled")
      return;
    const phase = hold.phase;
    const limit = phase === "waking" ? WAKE_MAX_MS : SEND_STALL_MS;
    const id = setTimeout(
      () => {
        if (phase === "waking") {
          // Release the in-flight latch with the phase it guards. `wake` clears
          // it in its own `finally`, which a wedged await never reaches — and
          // then every later Send returns at that guard, so the retry this
          // caption is about to offer would fire nothing at all, forever.
          wakeInFlightRef.current = false;
          setHold((h) =>
            h !== null && h.phase === "waking"
              ? { phase: "wakeFailed", text: h.text }
              : h,
          );
          return;
        }
        setHold((h) =>
          h !== null && h.phase === "sent"
            ? { phase: "stalled", text: h.text, clientMsgId: h.clientMsgId }
            : h,
        );
      },
      Math.max(0, limit - (Date.now() - hold.since)),
    );
    return () => clearTimeout(id);
  }, [hold]);

  // Read once per mount: sessionStorage isn't reactive, and a NEW seed for
  // this project only ever lands via a Refine pass, which is a different
  // route/mount than Chat.
  const seedTexts = useMemo(() => collectSeedTexts(projectId), [projectId]);

  const rows = useMemo<ReadonlyArray<ChatRow>>(() => {
    const out: ChatRow[] = [];
    for (const e of ws.timeline) {
      const r = rowOf(e, seedTexts);
      if (r) out.push(r);
    }
    return visibleChatRows(out, ws.agentBusy);
  }, [ws.timeline, seedTexts, ws.agentBusy]);

  // Freeze the rendered rows for the length of a hold. The wake's re-attach
  // reaches `setTimeline([])` and replays event by event, so without this the
  // whole transcript vanishes and rebuilds one row at a time under the lone
  // note, with the autoscroll firing on every increment. Row ids are
  // `${sessionId}#${seq}` and monotonic, so holding the longer list until the
  // replay catches up shows nothing stale — the same reasoning `useLatchedEntry`
  // is built on, scoped to one surface and one window.
  const frozenRef = useRef<ReadonlyArray<ChatRow>>(rows);
  if (hold === null) frozenRef.current = rows;
  const shown =
    hold !== null && rows.length < frozenRef.current.length
      ? frozenRef.current
      : rows;

  // Keep the latest turn in view. `scrollIntoView` walks up to whichever
  // ancestor scrolls (the FocusFrame's `.focus-stage`), so it works without a
  // ref into the frame.
  const bottomRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: "end" });
  }, [shown.length, ws.agentBusy, hold?.phase]);

  // Where each Figma chip goes: under EVERY message that carried its link (the
  // scan stamps each one's echo seq). A chip is a receipt beside what the user
  // did; at the foot of the transcript it drifted below every later reply and,
  // after a long one, sat behind the composer. Under only the newest message, a
  // re-paste took it away from the one the reply pointed at. The foot stays for
  // what cannot be placed — a replay written before `sourceSeq` existed, a
  // message this surface does not render, or an empty transcript (a link in a
  // chat whose session was revoked before the replay landed).
  const figmaPlacement = useMemo(() => {
    const anchors = new Set<number>();
    for (const m of shown) if (m.seq !== undefined) anchors.add(m.seq);
    return placeFigmaLinks(ws.figmaLinks, anchors);
  }, [shown, ws.figmaLinks]);

  // `ensure()` never produced a session — `ws` is wired to a null id and
  // parks at "closed"/sandbox phase "idle" forever, which reads as an
  // ordinary slow boot ("waking up") with no way out and no explanation for
  // why nothing ever arrives. Surface the refusal instead of falling through
  // to the normal chat body, and let `ensure()` be re-driven (idempotent) —
  // mirrors RefineFlow's own `session.error` branch.
  if (session.error !== null) {
    return (
      <FocusFrame stepKey="chat" railToggle onBack={onBack}>
        <Kicker>{t("common.loom")}</Kicker>
        <Headline>
          {session.accessDenied
            ? t("focus.status.accessDenied")
            : t("focus.refine.bootFailed.headline")}
        </Headline>
        <div className="focus-error">
          {/* A bare 403 on the mint proves the denial but names no leg — the
              GitLab pre-flight and Loom's own project gate both answer 403 —
              so the line stays on the consequence rather than sending the
              reader to fix a GitLab role that may already be in order. The
              probe-backed screen on FocusPage is the one that can be specific. */}
          {session.accessDenied
            ? t(accessDeniedHelpKey("unknown"))
            : t("focus.session.startFailedHelp")}
        </div>
        <div className="focus-footer">
          <button
            type="button"
            className="focus-btn"
            onClick={() => session.ensure()}
            disabled={session.starting}
          >
            {session.starting
              ? t("focus.refine.bootFailed.retrying")
              : t("focus.refine.bootFailed.retry")}
          </button>
        </div>
      </FocusFrame>
    );
  }

  const status: FocusStatus =
    ws.status === "denied"
      ? { tone: "bad", label: t("focus.status.accessDenied") }
      : ws.status === "expired"
      ? { tone: "bad", label: t("focus.status.sessionExpired") }
      : budgetDead
        ? { tone: "bad", label: t("focus.status.budgetStopped") }
        : // Paused, not broken and not working: `tone: "idle"` renders a static
          // grey dot. `"work"` would pulse amber and claim motion that is not
          // happening, `"bad"` would claim damage. One render ahead of the
          // sandbox snapshot's own `suspended`, and independent of it.
          ws.status === "idle_killed"
          ? { tone: "idle", label: t("focus.sandbox.suspended") }
          : !live
            ? {
                tone: "work",
                label:
                  phaseLabel(ws.sandbox.phase) ?? t("focus.status.starting"),
              }
            : ws.agentBusy
              ? { tone: "work", label: t("focus.status.thinking") }
              : { tone: "live", label: t("focus.status.live") };

  // One control, three places it can appear (transcript tail, budget wall, ended
  // block) — with the disclosure as VISIBLE text beside it, never a `title=`
  // tooltip: this is one click from a real GitLab CI pipeline, and a tooltip is
  // invisible on touch and absent from the button's accessible name.
  const newChatControl = (note: string, onClick: () => void = onNewChat) => (
    <div className="focus-chat-newchat">
      <span className="focus-note">{note}</span>
      <button type="button" className="focus-btn is-ghost" onClick={onClick}>
        {t("focus.chat.newChat")}
      </button>
    </div>
  );

  // The chips a message carried, under it. Every panel gets the whole list as
  // `allLinks`, so there is one connect card per chat and it sits beside the
  // link it is about.
  const figmaUnder = (m: ChatRow) => {
    const links =
      m.seq === undefined ? undefined : figmaPlacement.under.get(m.seq);
    if (links === undefined) return null;
    return (
      <div className={`focus-chat-figma is-${m.role}`}>
        <FigmaLinkPanel
          links={links}
          allLinks={ws.figmaLinks}
          onDecline={ws.sendFigmaConnectDeclined}
          onAllowFile={ws.sendFigmaAllowFile}
          // Chat is where the sketch has nothing to compete with: no question
          // is on screen, so a drawing under the chip costs the reader nothing.
          // Opted in per call site — the flow's own panel must NOT have it —
          // and gated on the SERVER's own capability, because whether the
          // drawing route exists is a deployment fact the client cannot know.
          // Without that gate the chip offered a sketch, fetched a 404, and told
          // the user "only the words were kept for this design, not the layout",
          // which is a claim about the DATA that a missing route cannot support.
          allowPreview={ws.figmaPreviewEnabled}
          sessionId={session.sessionId}
          // The picture THIS chat had, when the replay carried one. A chip
          // whose scene is in the history draws it with no request at all;
          // everything else falls back to the route exactly as before.
          sceneFor={ws.figmaSceneFor}
        />
      </div>
    );
  };

  // The only exit from a refused wake that keeps the paragraph. The record is
  // written with `handoff` and NO session stamp — the one shape a different
  // session restores from, which is why a rail pick (no flag, mismatched
  // stamp) cannot pull a sentence into the wrong conversation.
  const newChatWithDraft = () => {
    writeDraft(projectId, { sessionId: null, text: draft, handoff: true });
    onNewChat();
  };

  // `null` on a healthy chat. Otherwise: the cost disclosure while paused with
  // nothing held (Send is one keystroke from a real GitLab pipeline, and the
  // New-chat control three lines up makes that visible by house rule), then the
  // hold's own phase.
  const noteText: string | null =
    hold === null
      ? ws.status === "idle_killed"
        ? t("focus.chat.wakeCost")
        : stoppedWorkspace
          ? t("focus.chat.stoppedCost")
          : null
      : hold.phase === "waking"
        ? t("focus.chat.holdWaking")
        : hold.phase === "sent"
          ? t("focus.chat.holdSending")
          : hold.phase === "stalled"
            ? t("focus.chat.holdStalled")
            : t("focus.chat.wakeFailed");

  const proposalHeld = heldFor(proposalIid);
  // A row the user answered in their OWN words is waiting on Loom exactly like a
  // taken one: "Write my own" put its name line in the composer, and the Send
  // carried it. Read off the TIMELINE — any user turn the latest card has not
  // read yet that names the field — so it holds across a wake and a reload, and
  // it lasts while Loom is working on it or has not answered it yet. Without it
  // the row offered Loom's draft again the moment the user's own answer left,
  // one click from sending Loom's version right after theirs (#253, c08).
  const typedChecking = useMemo(() => {
    const out = new Set<string>();
    if (latestCard === null || issueOwned || proposalIid === null) return out;
    const stamp =
      latestCard.groundedThroughSeq ??
      (Number.isFinite(entrySeq(latestCard.id)) ? entrySeq(latestCard.id) : undefined);
    const fields = proposalRows.map((r) => r.field);
    for (let i = 0; i < ws.timeline.length; i += 1) {
      const e = ws.timeline[i]!;
      if (e.kind !== "user_message") continue;
      const seq = entrySeq(e.id);
      if (!Number.isFinite(seq) || (stamp !== undefined && seq <= stamp)) continue;
      const named = fields.filter((f) => composerNamesField(e.text, f));
      if (named.length === 0) continue;
      const answered = ws.timeline
        .slice(i + 1)
        .some((x) => x.kind === "assistant_turn" || x.kind === "readiness_card");
      if (!ws.agentBusy && answered) continue;
      for (const f of named) out.add(f);
    }
    return out;
  }, [latestCard, issueOwned, proposalIid, proposalRows, ws.timeline, ws.agentBusy]);
  const proposalSent: ReadonlySet<string> = new Set([
    ...sentFor(proposalIid),
    ...typedChecking,
  ]);
  const showProposal =
    proposalIid !== null &&
    !issueOwned &&
    !budgetDead &&
    !isSessionDead(ws.status) &&
    (proposalRows.length > 0 || proposalHeld.size > 0);
  const composerLocked = hold !== null && hold.phase !== "wakeFailed";

  const messageBar = (
    <div className="focus-msgbar-wrap">
      {showProposal && proposalIid !== null && (
        <ChatProposalBlock
          issueIid={proposalIid}
          rows={proposalRows}
          held={proposalHeld}
          sent={proposalSent}
          composer={draft}
          composerLocked={composerLocked}
          paused={paused}
          busy={ws.agentBusy}
          heldCount={answers.count}
          canSendNow={canSendHeld}
          onUse={(field, text) =>
            answers.add({ key: `${String(proposalIid)}:${field}`, field, text })
          }
          onUndo={(field) => answers.undo(`${String(proposalIid)}:${field}`)}
          onWriteOwn={(field) => {
            const line = chatNameLineOf(field);
            if (line !== null) putInComposer(`${line}\n`);
          }}
          onChangeList={(_field, text) => putInComposer(text)}
          onSendNow={() => sendChat("", true)}
        />
      )}
      {noteText !== null && (
        // The live region wraps the CAPTION ONLY. The user's own sentence is in
        // the composer below, and a caption change must never read it back to
        // them.
        <div className="focus-msgbar-note" role="status" aria-live="polite">
          <span className="focus-note">{noteText}</span>
          {hold?.phase === "stalled" && (
            // `reconnect`, never a re-send: the frame is already in the outbox
            // under its original id, which the reconnect redelivers and the
            // server's ingress dedupe makes free. A fresh Send would mint a new
            // id, walk past that dedupe and buy a duplicate model turn.
            <button
              type="button"
              className="focus-btn is-ghost"
              onClick={ws.reconnect}
            >
              {t("focus.chat.holdRetry")}
            </button>
          )}
        </div>
      )}
      <MessageBar
        // The latch, not just the timeline scan: `useWsSession.send` does NOT
        // append optimistically — the server echoes user input back onto the bus
        // — so between a send and that echo the chat still reads as unspoken. A
        // user who sends and immediately hits Zurück would unmount with
        // `spoke: false`, fire the detach, and have the grace reaper cancel the
        // pipeline mid-turn. The timeline scan above stays as the
        // reload-survivable backstop.
        //
        // It is latched WHERE THE FRAME GOES OUT — here and in the flush — and
        // deliberately not at the top of the handler: a Send that only enters
        // the hold has sent nothing, and disarming the detach for it hands a
        // sandbox the wake just triggered to the 60-minute idle horizon with
        // zero turns run, where the 90s detach grace would have reclaimed it.
        // The held answers from Loom's proposal ride the SAME message: one
        // turn, not one for them and a second that kills it.
        onSend={(text) => sendChat(text, true)}
        inputRef={composerRef}
        value={draft}
        onValueChange={onDraftChange}
        // Held, not dead: the text stays black and selectable and the caret
        // survives, which is what makes the wait read as an ordinary send.
        readOnly={hold !== null && hold.phase !== "wakeFailed"}
        // Deliberately NOT widened to plain `closed`: that status has no
        // explanation on screen and no held text behind it.
        disabled={
          budgetDead ||
          isSessionDead(ws.status) ||
          !(ws.status === "open" || ws.status === "idle_killed" || hold !== null)
        }
        placeholder={
          budgetDead
            ? t("focus.chat.budgetPlaceholder")
            : ws.status === "idle_killed"
              ? t("focus.chat.pausedPlaceholder")
              : t("focus.chat.placeholder")
        }
      />
    </div>
  );

  return (
    <>
      {/* The COUNTDOWN only. The killed state has no dialog and nothing to
          click through — the composer below stays live and Send is the resume.
          The pre-kill warning stays because it is the one affordance that lets
          someone avoid paying for a reboot at all, and any keystroke already
          cancels it. */}
      {ws.idleCountdownEndsAt !== null && (
        <IdleReconnectDialog
          endsAt={ws.idleCountdownEndsAt}
          killed={false}
          onStay={ws.dismissIdle}
        />
      )}
      <FocusFrame
      stepKey="chat"
      railToggle
      onBack={onBack}
      status={status}
      wide
      messageBar={messageBar}
    >
      {/* Three independent blocks, not one `rows.length` ternary. A revoked
          session's WS attach sends `session_expired` and closes BEFORE any event
          replay, so an expired chat routinely has an empty timeline — and inside
          the ternary the ended block below would never render there, leaving the
          user on "Chat with Loom" with a red pill, a silently disabled composer
          and no control at all. */}
      {ws.status !== "expired" && shown.length === 0 && (
        <>
          <Kicker>{t("common.loom")}</Kicker>
          <Headline>{t("focus.chat.headline")}</Headline>
          <Help>{t("focus.chat.help")}</Help>
        </>
      )}
      {shown.length > 0 && (
        <div className="focus-chat">
          {shown.map((m) =>
            // A synthetic seed reads as neither party's turn: it is Loom's own
            // brief to itself, stored as a `user_message` so the MODEL treats
            // it as the conversation's opener — but showing it under "You"
            // hands the user their own words back with seven sentences of
            // machine instruction stitched on, "propose the readiness card"
            // included. `evidence` is the byte-exact span that IS the user's
            // (may be empty — an issue picked from search declares none); the
            // rest sits behind a disclosure, same "hide the machinery, keep it
            // reachable" shape `BreakdownCard`'s own failure detail uses.
            m.role === "seed" ? (
              <Fragment key={m.id}>
                <div className="focus-chat-msg is-seed">
                  <span className="focus-chat-who">
                    {t("focus.chat.seedLabel")}
                  </span>
                  {m.evidence !== undefined && m.evidence.trim() !== "" && (
                    <p className="focus-chat-text focus-chat-seed-evidence">
                      {m.evidence}
                    </p>
                  )}
                  <details className="focus-chat-seed-detail">
                    <summary>{t("focus.chat.seedDetail")}</summary>
                    <p className="focus-chat-text">{m.text}</p>
                  </details>
                </div>
                {figmaUnder(m)}
              </Fragment>
            ) : (
              <Fragment key={m.id}>
                <div className={`focus-chat-msg is-${m.role}`}>
                  {m.role !== "meta" && (
                    <span className="focus-chat-who">
                      {m.role === "you" ? t("common.you") : t("common.loom")}
                    </span>
                  )}
                  {m.role === "loom" ? (
                    // Model turns are Markdown; user and meta rows stay literal
                    // text (a user's `*` or `#` is content, not formatting).
                    // This is the same warm PM session as the dashboard chat, so
                    // the agent's ```loom-confirm fence must become the
                    // Accept-changes button here too, not an inert code block.
                    // Under the UI public-name mode the model's "Loom" shows as
                    // the public name (display only; code and fences untouched).
                    <MarkdownView
                      source={uiProse(m.text)}
                      className="focus-chat-text"
                      components={loomConfirmComponents(m.id, ws.send)}
                    />
                  ) : (
                    <span className="focus-chat-text">{userRowText(m)}</span>
                  )}
                </div>
                {figmaUnder(m)}
              </Fragment>
            ),
          )}
          {/* The ended block below wins over the wall: both carry a "New chat"
              control, and a session that is BOTH budget-exhausted and revoked
              would otherwise render two identical buttons under contradictory
              sentences (and break every `getByRole("button", {name})` lookup).
              "This chat has ended" is the more specific fact — the same reason
              a rail refusal wins over `entry.warm`. */}
          {budgetDead && ws.status !== "expired" ? (
            <div className="focus-chat-budget">
              <span className="focus-chat-text focus-error">
                {t("focus.chat.budgetExhausted")}
              </span>
              {ws.budgetExhausted !== null && (
                <span className="focus-note">
                  {t("focus.refine.budget.spent", {
                    used: ws.budgetExhausted.used.toFixed(2),
                    limit: ws.budgetExhausted.limit.toFixed(2),
                  })}
                </span>
              )}
              {/* Same honesty as the refine wall, with the scope this surface
                  actually has: the ceiling belongs to THIS chat's own session,
                  and the issues opened in this tab spend against a different
                  one. */}
              <span className="focus-note">
                {t("focus.chat.budgetSharedNote")}
              </span>
              <button type="button" className="focus-btn" onClick={onNewChat}>
                {t("focus.chat.newChat")}
              </button>
            </div>
          ) : (
            ws.agentBusy && (
              <div className="focus-chat-msg is-loom">
                <ThinkingDots label={t("focus.chat.thinking")} />
              </div>
            )
          )}
          {/* No "New chat" at the tail of a HEALTHY chat: the rail carries it
              now, on every Focus surface rather than only inside a conversation
              — which is also the only place it can be offered to someone who
              has no chat open. What stays here are the two dead ends, where the
              rail is a drawer the user would have to know to open: the budget
              wall above, and the ended block below. */}
          <div ref={bottomRef} />
        </div>
      )}
      {/* The Figma chips no message could hold (see `figmaPlacement`): a
          replay from before `sourceSeq`, or an empty transcript. The chips are
          folded from replayed `figma_link_state` events rather than from
          timeline entries, so they must still render here. The panel returns
          null when there is nothing left to place. */}
      <FigmaLinkPanel
        links={figmaPlacement.foot}
        allLinks={ws.figmaLinks}
        onDecline={ws.sendFigmaConnectDeclined}
        onAllowFile={ws.sendFigmaAllowFile}
        // Server-advertised; see the panel above.
        allowPreview={ws.figmaPreviewEnabled}
        sessionId={session.sessionId}
        // See the panel above.
        sceneFor={ws.figmaSceneFor}
      />
      {/* `expired` only, never `isSessionDead(ws.status)`: a `denied` follows
          the IDENTITY, so a fresh session is refused identically and offering
          one would be a dead end (`focus.refine.sessionDenied` sets that
          precedent). Offered regardless of `spoke` — a closed chat is exactly
          when a new one is the only way forward — which is why it uses
          `newChatCost` rather than `newChatNote`: an unspoken chat is
          `kind=unknown` and will not appear in the rail. */}
      {ws.status === "expired" && (
        <div className="focus-chat-ended" role="status">
          <span className="focus-chat-text focus-error">
            {t("focus.chat.sessionEnded")}
          </span>
          {newChatControl(t("focus.chat.newChatCost"))}
        </div>
      )}
      {/* The wake was refused (409 on a discarded session, a 403, a dead
          network). Nothing was sent, the text is still in the composer and
          still editable, and Send re-runs the wake — so this offers the OTHER
          way out, and carries the paragraph with it. */}
      {hold?.phase === "wakeFailed" && (
        <div className="focus-chat-ended">
          {/* Its OWN headline, short. The caption above the composer already
              carries the instruction ("press Send to try again"), and the same
              sentence printed twice forty pixels apart reads as a rendering
              fault and pushes the control it belongs to off a phone screen. */}
          <span className="focus-chat-text focus-error">
            {t("focus.chat.wakeFailedTitle")}
          </span>
          {newChatControl(t("focus.chat.newChatCost"), newChatWithDraft)}
        </div>
      )}
      {/* A dispatched frame that never landed. The composer stays held —
          re-sending would mint a new delivery id and buy a duplicate turn — so
          this is the only exit that is not a reconnect, and it carries the
          paragraph with it. It is here rather than beside the caption because
          the price has to be visible next to the button, same as everywhere
          else this control appears. */}
      {hold?.phase === "stalled" &&
        newChatControl(t("focus.chat.newChatCost"), newChatWithDraft)}
    </FocusFrame>
    </>
  );
}
